import moment from "moment";
import { Breakdowns } from "../interface/breakdowns";
import { Checklist } from "../interface/checklist";
import { Client, CLIENT_ACTIVE_STATUS } from "../interface/client";
import { Comment } from "../interface/comment";
import { CustomState } from "../interface/custom-state.model";
import { Note } from "../interface/note";
import { Operation } from "../interface/operation";
import { Pdf } from "../interface/pdf";
import { RoLaborFull } from "../interface/ro-labor-full";
import { RoLaborTemp } from "../interface/ro-labor-temp";
import { DESCRIPTIVE_ITEM_STATUS, RoPartTemp } from "../interface/ro-part-temp";
import { Tag } from "../interface/tag";
import { ThirdPartyService } from "../interface/third-party-service";
import { Vehicle } from "../interface/vehicle";
import { authorizationTypes } from "../shared/lists/authorization-types";
import { laborStates } from "../shared/lists/labor-states";
import { partStates } from "../shared/lists/part-states";
import { RO_STATES } from "../shared/lists/ro-states";
import { RoStateGroup } from "../shared/pipes/ro-state-group.pipe";
import { ObjectId } from "../shared/type-aliases/object-id";
import { Utilities } from "./utilities";
import { MkgOSPart } from "./mkg-os-part";
import { PaymentCondition } from "../interface/paymentCondition";
import { Money } from "./money";
import { Budgeter, IConultantSimplified } from "../interface/budgeter";
import { MatDialog } from "@angular/material/dialog";
import { AveragePurchaseCostAlertComponent } from "../component/dialog/average-purchase-cost-alert/average-purchase-cost-alert.component";
import { Part } from "../interface/part";
import { LayoutService } from "../service/layout.service";
import { checklistAssig, RoType } from "../interface/ro-type";
import type { ChecklistMediaQualityPreset } from "@app/shared/config/checklist-media-quality.config";
import { ApiFinanceIN } from "../interface/ro-finance";
import { MigrateService, TaxesParams } from "../service/migrate.service";
import { Company } from "../interface/company";
import { Decimal } from "decimal.js"
import { Farm } from "../interface/farm";
import { Labor } from "../interface/labor";
import { ClientUtilities } from "./client-utilities";
import { TimeTracker } from "../interface/time-tracker";
import { OperationFunction } from "../shared/lists/operation-functions";
import { IND_PRES } from "../shared/enums/fiscal/ind-pres";
import { BOOLEAN_TWO } from "../shared/common-enums/boolean-two";
import { VehicleUtilities } from "./vehicle-utilities";
import { BehaviorSubject, combineLatest, skip } from "rxjs";
import { NF_response } from "@app/interface/NF-response";
import { NFSE_response } from "@app/interface/NFSE-response";
import { toASCII } from "punycode";
import { IND_INTERMED } from "@app/shared/enums/fiscal/ind-intermed";
import { JOIN_CHAR } from "@app/service/ro.service";


export enum CHECKLIST_OPTIONS {
    UNSET,
    COMMON,
    BY_MODEL,
}

export const NFCE_CONSUMER_ISSUE_LIMIT = 10_000;

export enum OS_BLOCK {
    "Blocked" = 1, // Bloqueada
    "Released" // Liberada
}

export interface NfseCustomDestination {
    document?: string;
    documentType?: number;
    nif?: string;
    nonNifReason?: 0 | 1 | 2;
    name?: string;
    address?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    phone?: number;
    email?: string;
    cityCode?: number;
    cityName?: string;
    zipCode?: string;
    country?: string;
    stateProvinceRegion?: string;

    clientId?: string;
}


export interface IOsClient {
    address: {
        cep: string;
        complement?: string;
        city: string;
        neighborhood: string;
        number: string;
        state: string;
        street: string;
    };
    active: CLIENT_ACTIVE_STATUS;
    document: string;
    documentType: number;
    emailIntegrated: string;
    farm: Farm[];
    lang: number;
    maritalStatus: number;
    name: string;
    phone1: string;
    phone2: string;
    whatsapp?: string;
    rg?: string;
    type: string;
    id: string;
    email?: string;
    fancyName?: string;
    availableLimit?: number;
    creditLimit?: number;
    taxpayer?: boolean;
    retPIS?: boolean;
    retCOFINS?: boolean;
    retIRPJ?: boolean;
    retINSS?: boolean;
    retCSLL?: boolean;
    observation?: string
};

export interface IOSVehicle {
    brand: string;
    cilindradas: string;
    color: string;
    fuel: string;
    id: string;
    model: string;
    modelYear: string;
    motor: string;
    plate: string;
    vin: string;
    year: string;
    /** @TODO test if working */
    note?: string;
}

export interface IOSOperation {
    _id: string;
    id: string;
    description: string;
    docFiscal: boolean;
    docFiscalServico: boolean;
    /** In the api the 'operationFunction' is 'function' */
    function: string;
    /** In the api the 'entry' is 'inOut' */
    inOut: boolean;
    /** In the api the 'generateFinancial' is 'financialGenera' */
    financialGenera: boolean;
    /** In the api the 'changeStock' is 'moveStock' */
    moveStock: boolean;
    /** In the api the 'entry' is 'inOut' */
    entry: boolean;
    /** In the api the 'operationFunction' is 'function' */
    operationFunction: OperationFunction;

    /** In the api the 'changeStock' is 'moveStock' */
    changeStock?: boolean;
    /** In the api the 'generateFinancial' is 'financialGenera' */
    generateFinancial?: boolean;

    PIS?: {
        Aliquot?: number;
        valMinRet?: number;
    };
    COFINS?: {
        Aliquot?: number,
        valMinRet?: number,
    };
    CSLL?: {
        aliquot?: number;
        valMinRet?: number;
    };
    IRPJ?: {
        aliquotLabor?: number;
        valMinRet?: number;
    };
    INSS?: {
        aliquot?: number;
        valMinRet?: number;
    };
    ICMS: {
        CST?: string;
        Message?: string;
        Aliquot?: number;
        CFOP?: string;
        CFOP_I?: string;
        // readjustment?: number;
    };
}

export interface IOSRoType {
    id: string;
    _id: string;
    observation: string;
    type: string;
    checklistAssig: checklistAssig;
    nfseCnpj: string;
    nfeCnpj: string;
    operation: string;
    checklistMediaQualityPreset?: ChecklistMediaQualityPreset;
}

export interface OsLaborPopulate {
    id?: string;
    description?: string;
    code?: string;
    IRPJ?: { aliquotLabor?: Labor['IRPJ']['aliquotLabor'] };
    CSLL?: { aliquot?: Labor['CSLL']['aliquot'] };
    INSS?: { aliquot?: Labor['INSS']['aliquot'] };
    CNAE?: Labor['CNAE'];
    LCCode?: Labor['LCCode'];
    KmToChange?: Labor['KmToChange'];
    monthToChange?: Labor['monthToChange'];
    saleValue?: Labor['saleValue'];
    group?: Labor['group'];
}

export interface OsLabor {
    id?: string;
    labor?: OsLaborPopulate;
    amount?: number;
    saleValue?: number;
    available?: number;
    observation?: string;
    liquidValue?: number;
    additionalDiscount?: number;
    mechanic?: string;
    /** Array of mechanic IDs for companies using multiple mechanics per service */
    mechanics?: string[];
    /** Chief mechanic ID for companies using multiple mechanics per service */
    chiefMechanic?: string;
    timeTrackers?: TimeTracker[];
    discountValue?: number,
    internalObservation?: string;
    discountProp?: number,
    discountPropGeral?: number,

    /** @local only */
    oldAmount?: number;
    /** @local only */
    oldUnitValue?: number;
    /** @local only */
    blocked?: boolean;
    /** @local only */
    running?: boolean;
}


/** Onda1·E1·T4. Uma baixa do recibo (`config/models/lows.js`). `value` chega em **centavos**,
 * mesma unidade de `ApiReceipt.value` — confirmado em `companies-lows.js` (`register()`, que grava
 * `Math.floor(body.value * 100)`). */
export interface ApiReceiptLow {
    id?: string;
    value?: number;
    date?: string;
    paymentMethod?: number;
}

/** Onda1·E1·T4. `os.receipts` populado (`config/models/receipts.js`, ver T1). `value` chega em
 * **centavos** — mesma unidade de `ApiOS.liquidValue`, normalizada no construtor do `MkgOS` junto
 * com `lows[].value`. Sem essa normalização, RECEBIDO/A RECEBER saem 100× maiores. */
export interface ApiReceipt {
    id?: string;
    client?: string;
    name?: string;
    value?: number;
    dueDate?: string;
    type?: number;
    owner?: string;
    lows?: ApiReceiptLow[];
}

export interface ApiOS {
    authorizationType?: number;
    /** Instante em que `handleAuthorization`/`handleRejection` decidem o orçamento (aprovação ou
     * rejeição) — `os-approval-service.ts` no apimekanicgo. */
    authorizationDate?: string;
    id?: string;
    type?: IOSRoType;
    code?: number;
    problem?: string;
    currentState?: number;
    client?: IOsClient;
    vehicle?: IOSVehicle;
    checklist?: any[];
    breakdowns?: any;
    checklistModel?: CHECKLIST_OPTIONS;
    nfseCustomDestination?: NfseCustomDestination;
    intermediary?: IND_INTERMED;
    parts?: {
        id?: string;
        part?: Part;
        amount?: number;
        purchaseValue?: number;
        purchaseAverage?: number;
        saleValue?: number;
        observation?: string;
        available?: number;
        liquidValue?: number;
        additionalDiscount?: number;
        // fixedValue: number;
        mechanic?: string;
        seq?: number;
        timeTrackers?: any[];
        discountValue?: number;
        internalObservation?: string;
        delivery?: number;
        unitMeasure?: string;
    }[];
    labors?: OsLabor[];
    liquidValue?: number;
    createdAt?: string;
    discountParts?: number;
    discountLabors?: number;
    notes?: any[];
    shortUrl?: string;
    /** Onda1·E1·T4: objeto populado (T1) quando a OS já passou pela aprovação; `undefined`/`null`
     * antes disso. Endpoints que não populam ainda devolvem o id como string. */
    receipts?: ApiReceipt | string;
    companyLogo?: string;
    telefTmp?: string;
    companyInfo?: string;
    user?: string;
    updatedAt?: string;
    lastUpdate?: string;
    laborsTMP?: any[];
    partsTMP?: any[];
    budgetCode?: number;
    km?: number;
    operation?: IOSOperation;
    generalDiscount?: number;
    nfseIndDest?: 0 | 1;
    presence?: string;
    paymentConditions?: {
        /** send a string, receive a object */
        paymentCondition?: string | PaymentCondition;
        value?: number;
        _id?: string;
        jsonAny?: string;
        cardInfo?: any;
    }[]
    /** Fenced revision of `paymentConditions`, used by the atomic replace endpoint to detect stale edits */
    paymentConditionsRevision?: number;
    thirdPartyServices?: any[];
    clientTmp?: string;
    vehicleTmp?: string;
    prisma?: string;
    tags?: any[];
    chat?: any[];
    valueDiscountParts?: number;
    valueDiscountLabors?: number;
    valueDiscountOs?: number;
    grossValue?: number;
    liquidValueParts?: number;
    grossValueParts?: number;
    liquidValueLabors?: number;
    grossValueLabors?: number;
    observation?: string;
    observationNFSE?: string;
    obsClient?: string;
    budgeter?: Budgeter;
    consultant?: IConultantSimplified;
    importsBudget?: string[];
    valDeducoes?: number;
    valDeducoesParts?: number;
    obsRejeicao?: string;
    rejectType?: string;
    groupId?: string;
}

export type ApiOSPost = Omit<ApiOS, 'client' | 'vehicle' | 'operation' | 'type'> & {
    client: string;
    vehicle: string;
    operation: string;
    type: string;
}

export type TaxesParamsOS = Omit<TaxesParams, 'operation' | 'client'> & {
    operation: IOSOperation;
    client: IOsClient
}

export interface MkgOSFromFormInput {
    id: string | null;
    user: string;
    km: number | string;
    deliveryForecast?: string | null;
    problem: string;
    prisma: string;
    clientTmp: string;
    /** The plate of temporary vehicle not registered + the note of this vehicle (for Agro and nautical) */
    vehicleTmp: string;
    consultant: string;
    telefTmp?: string;
    type?: string;
    vehicle?: string;
    client?: string;
    operation?: string;
    currentState?: RO_STATES;
    /**
     * The note in OS is the machine observation for Agro and Nautical environments
     * In commom cases the note is stored into Vehicle.note, but when user don't define the machine of the OS
     */
    note?: string;
}

export interface MkgOSFromFormDraft extends Omit<MkgOSFromFormInput, 'id'> {
    id?: string;
}

/**
 * How the vehicle in a ro-create draft was selected:
 * - `plate-lookup`: plate typed directly on the budget/OS form
 * - `vehicle-search`: vehicle chosen on the vehicle search screen
 * - `vehicle-edit`: vehicle explicitly saved through create/edit, whose client is authoritative
 */
export type VehicleSelectionSource = 'plate-lookup' | 'vehicle-search' | 'vehicle-edit';

/**
 * How the client in a ro-create draft was selected:
 * - `client-save`: client successfully saved through the client registration screen
 */
export type ClientSelectionSource = 'client-save';

/**
 * Reverse-router draft of the ro-create form. `vehicleSelectionSource` and
 * `clientSelectionSource` are transient: they must be consumed (and deleted) during
 * draft restoration and never reach an OS create/update payload —
 * `MkgOS.fromForm()` only copies the known form fields.
 */
export interface RoCreateReverseDraft extends Partial<MkgOSFromFormInput> {
    vehicleSelectionSource?: VehicleSelectionSource;
    clientSelectionSource?: ClientSelectionSource;
}

export class MkgOS {
    id?: string;
    type?: IOSRoType;
    code?: number;
    codeSystem?: number;
    problem?: string;
    authorizationType?: number;
    /** @see ApiOS.authorizationDate */
    authorizationDate?: string;
    client?: IOsClient;
    vehicle?: IOSVehicle;
    appointment?: string;
    deliveryForecast?: string;
    intermediary?: IND_INTERMED;
    breakdowns?: Breakdowns;
    nfseIndDest?: 0 | 1;
    /** @see authorizationTypes */
    complementaryApprove?: number;
    createdAt?: string;
    priority?: number;
    budgeter?: string;
    showPDF?: number;
    shortUrl?: string;
    startDate?: string;
    receiveDate?: string;
    evaluationDate?: string;
    evaluation?: number;
    importsBudget?: string[];
    obsRejeicao?: string;
    rejectType?: string;
    groupId?: string;
    presence?: IND_PRES = IND_PRES.PRESENCIAL;
    checklistModel?: CHECKLIST_OPTIONS;
    obsClient?: string;
    nfseCustomDestination: NfseCustomDestination;

    /** Onda1·E1·T4: primeiro leitor de `ro.receipts` no front (levantamento do T1 — nenhum outro
     * código no repo lia este campo). Objeto populado (`ApiReceipt`) quando a OS já foi aprovada;
     * `undefined`/`null` antes disso, `string` (id) só em endpoints que ainda não populam. `value`
     * e `lows[].value` já chegam em reais aqui — normalizados no construtor, junto com os demais
     * campos monetários (`liquidValue`, `thirdPartyServices[].collectedValue`). */
    receipts?: ApiReceipt | string;

    /** used into WebClient app */
    companyLogo?: string;
    observation?: string;
    observationNFSE?: string;
    km?: number;
    recall?: string;
    authDisposal?: BOOLEAN_TWO;

    /** id of checklist-model-item applied to this OS */
    checklistModelItem?: string;

    /** id of survey applied to this OS */
    surveyModelItem?: string

    /**
     * A string cointaining the company cnpj, the company name and company site, joined by two asterisks (**)
    *
    * Used in WebCliente
    * @example"companycnpj**companyname**companysite" */
    companyInfo?: string;

    /** Email of the user that opened the buget (Consultor)*/
    user?: string;
    updatedAt?: string;
    lastUpdate?: string;
    evaluationText?: string;
    pdf?: Pdf;
    operation?: IOSOperation;
    additionalState?: CustomState | string;

    /** @test */
    prisma?: string;
    /** @todo */
    model?: string;
    /** @todo */
    serie?: string;

    packet?: string;
    review?: string;
    menu?: string;
    checklist?: Checklist[] = [];
    private _parts?: MkgOSPart[] = [];
    public get parts(): MkgOSPart[] { return this._parts }
    public set parts(value: MkgOSPart[]) { this._parts = value }
    labors?: OsLabor[] = [];
    chat?: Comment[] = [];
    tags?: Tag[] = [];
    notes?: Note[] = [];
    partsTMP?: RoPartTemp[] = [];
    laborsTMP?: RoLaborTemp[] = [];
    thirdPartyServices?: ThirdPartyService[] = [];
    paymentConditions?: ApiFinanceIN[] = [];
    paymentConditionsRevision?: number = 0;

    /** When use defalt client, show this value as client name */
    clientTmp?: string;

    /** When use defalt client, show this value as the client phone */
    telefTmp?: string;

    // Discount Control
    osBlock?: OS_BLOCK;

    // the id of selected farm when document type of client is rural producer
    idFarmer?: ObjectId;

    additionalStateObject?: CustomState;
    operationId?: string;
    budgeterObject?: Budgeter;
    /**@deprecated */
    clientObject?: Client;
    /**@deprecated */
    vehicleObject?: Vehicle;
    budgetCode?: number;
    partsExpenseValue?: number;
    consultant?: ObjectId;

    private _vehicleTmp: string;
    private _vehicleTmpPlate: string;
    private _vehicleTmpNote: string;
    get vehicleTmp() {
        return this._vehicleTmpPlate;
    }
    get note() {
        return this._vehicleTmpNote;
    }
    set vehicleTmp(value) {
        if (typeof value !== 'string') {
            value = '';
        }
        this._vehicleTmpPlate = value.split(JOIN_CHAR)[0];
        this._vehicleTmpNote = value.split(JOIN_CHAR)[1];
        this._vehicleTmp = value;
    }

    /**
     * The value calculated for DEDUCTION of ISS retention
     * @readonly Please call the function `calculateRetentions()` to define it
     */
    get retISS() { return this._retISS }
    private set retISS(value) { this._retISS = value }
    private _retISS = 0;

    /**
     * The sum of `itemVlDed` of available OS labors
     * @readonly Please call the function `calculateRetentions()` to define it before create the titles
    */
    get valDeducoesLabors() { return this._valDeducoesLabors }
    private set valDeducoesLabors(value) { this._valDeducoesLabors = value }
    private _valDeducoesLabors = 0;

    /**
     * The sum of `itemVlDed` of available OS parts
     * @readonly Please call the function `calculateRetentions()` to define it before create the titles
    */
    get valDeducoesParts() { return this._valDeducoesParts }
    private set valDeducoesParts(value) { this._valDeducoesParts = value }
    private _valDeducoesParts = 0;

    // used for integrated company
    private isIntegratedCompany: boolean = false;
    canEditOs$ = new BehaviorSubject(false);

    readonly nfInvoices$ = new BehaviorSubject<NF_response[]>([]);
    readonly nfseInvoices$ = new BehaviorSubject<NFSE_response[]>([]);
    readonly areInvoicesConsulted$ = new BehaviorSubject<boolean>(false);

    /**
     * Sum reduction values of all calculated retentions from a preview
     * of the retentions of each available item of this OS.
     *
     * This method define the values for follow properties:
     * - retISS
     * - valDeducoes
     * - valDeducoesParts
     *
     * The values are taken from the same functions on MigrateService, where taxes are calculated.
     *
     * Please call this method before create titles for this OS, after update the client of OS
     * and after update the operation.
     *
     * @param companyLabors The company used to emit the labors invoice
     * @param companyParts The company used to emit the parts invoice, if ommited the companyLabors will be used instead
     * @returns Boolean properties indicating if this OS and items should be updateds into database.
     */
    calculateRetentions(companyLabors: Company, _companyParts?: Company): {
        issRetChanged: boolean,
        valDeductionsLaborsChanged: boolean,
        valDeductionsPartsChanged: boolean
    } {
        if (!this.client || !this.operation) {
            return {
                issRetChanged: false,
                valDeductionsLaborsChanged: false,
                valDeductionsPartsChanged: false
            }
        }

        // if(!companyParts){
        //     companyParts = companyLabors;
        // }
        /**
         * @todo get two operations for two invoices
         */


        // -----------------------------------------
        // for parts, we don't calculate reductions
        // -----------------------------------------
        let deductionsPartsSum = 0;
        // if(companyParts.deduNFE == BOOLEAN_ENUM.TRUE){
        //     const retTrib = MigrateService.getRetTrib(
        //         this.liquidValueParts,
        //         this.operation,
        //         this.clientObject,
        //         this.availableParts);

        //     if (retTrib) {
        //         deductionsPartsSum = Money((retTrib.vRetPIS || 0) // PIS
        //             + (retTrib.vRetCOFINS_servttlnfe || 0) // COFINS
        //             + (retTrib.vRetCSLL || 0) // CSLL
        //             + (retTrib.vIRRF || 0) // IR
        //             + (retTrib.vRetPrev || 0)); // INSS
        //     }
        // }


        const retISS = this.client?.taxpayer && this.liquidValueLabors >= companyLabors.minISS;
        const retPIS = this.client?.retPIS && this.liquidValueLabors >= this.operation?.PIS?.valMinRet;
        const retCOFINS = this.client?.retCOFINS && this.liquidValueLabors >= this.operation?.COFINS?.valMinRet;
        const retCSLL = this.client?.retCSLL && this.liquidValueLabors >= this.operation?.CSLL?.valMinRet;
        const retIRPJ = this.client?.retIRPJ && this.liquidValueLabors >= this.operation?.IRPJ?.valMinRet;
        const retINSS = this.client?.retINSS && this.liquidValueLabors >= this.operation?.INSS?.valMinRet;

        const retentions = {
            ISS: 0,
            ISSBaseCalc: 0,
            PIS: 0,
            COFINS: 0,
            CSLL: 0,
            IRPJ: 0,
            INSS: 0,
        }

        for (const osLabor of this.availableLabors) {
            const taxesParams: TaxesParamsOS = {
                client: this.client,
                operation: this.operation,
                ItemBaseCalculo: osLabor.liquidValue,
                labor: osLabor.labor
            }
            if (retISS) {
                retentions.ISSBaseCalc = Decimal.add(retentions.ISSBaseCalc, taxesParams.ItemBaseCalculo).toNumber();
                const issTaxes = MigrateService.getISStaxes({ company: companyLabors, ...taxesParams })
                retentions.ISS = Decimal.add(retentions.ISS, issTaxes.ItemvIss).toNumber();
            }
            if (retPIS) {
                const pisTaxes = MigrateService.getPIStaxes(taxesParams);
                retentions.PIS = Decimal.add(retentions.PIS, pisTaxes.ItemValPIS || 0).toNumber();
            }
            if (retCOFINS) {
                const cofinsTaxes = MigrateService.getCOFINStaxes(taxesParams);
                retentions.COFINS = Decimal.add(retentions.COFINS, cofinsTaxes.ItemValCOFINS || 0).toNumber();
            }
            if (retCSLL) {
                const csllTaxes = MigrateService.getCSLLtaxes(taxesParams);
                retentions.CSLL = Decimal.add(retentions.CSLL, csllTaxes.ItemValCSLL || 0).toNumber();
            }
            if (retIRPJ) {
                const irTaxes = MigrateService.getIRtaxes(taxesParams);
                retentions.IRPJ = Decimal.add(retentions.IRPJ, irTaxes.ItemValIR || 0).toNumber();
            }
            if (retINSS) {
                const inssTaxes = MigrateService.getINSStaxes(taxesParams);
                retentions.INSS = Decimal.add(retentions.INSS, inssTaxes.ItemValINSS || 0).toNumber();
            }
        }


        for (const outsourced of this.thirdPartyServices) {
            const taxesParams: TaxesParamsOS = {
                client: this.client,
                operation: this.operation,
                ItemBaseCalculo: outsourced.collectedValue,
            }
            if (retISS) {
                const issTaxes = MigrateService.getISStaxes({ company: companyLabors, ...taxesParams })
                retentions.ISS = Decimal.add(retentions.ISS, issTaxes.ItemvIss || 0).toNumber();
            }
            if (retPIS) {
                const pisTaxes = MigrateService.getPIStaxes(taxesParams);
                retentions.PIS = Decimal.add(retentions.PIS, pisTaxes.ItemValPIS || 0).toNumber();
            }
            if (retCOFINS) {
                const cofinsTaxes = MigrateService.getCOFINStaxes(taxesParams);
                retentions.COFINS = Decimal.add(retentions.COFINS, cofinsTaxes.ItemValCOFINS || 0).toNumber();
            }
            if (retCSLL) {
                const csllTaxes = MigrateService.getCSLLtaxes(taxesParams);
                retentions.CSLL = Decimal.add(retentions.CSLL, csllTaxes.ItemValCSLL || 0).toNumber();
            }
            if (retIRPJ) {
                const irTaxes = MigrateService.getIRtaxes(taxesParams);
                retentions.IRPJ = Decimal.add(retentions.IRPJ, irTaxes.ItemValIR || 0).toNumber();
            }
            if (retINSS) {
                const inssTaxes = MigrateService.getINSStaxes(taxesParams);
                retentions.INSS = Decimal.add(retentions.INSS, inssTaxes.ItemValINSS || 0).toNumber();
            }
        }

        // sum labors retentions
        const federalRetentions = new Decimal(0)
            .plus(retentions.PIS)
            .plus(retentions.COFINS)
            .plus(retentions.CSLL)
            .plus(retentions.IRPJ)
            .plus(retentions.INSS);

        let issDeduction = 0;
        if (companyLabors.deduISS) {
            issDeduction = retentions.ISS
        }

        let federalDeductions = 0;
        if (companyLabors.deduNFSE) {
            federalDeductions = Decimal(federalDeductions).plus(federalRetentions).toNumber();
        }

        const valDeductionsLaborsChanged = !Decimal(federalDeductions).equals(this.valDeducoesLabors);
        const valDeductionsPartsChanged = deductionsPartsSum !== this.valDeducoesParts;
        if (valDeductionsPartsChanged) {
            this._valDeducoesParts = deductionsPartsSum;
        }
        if (valDeductionsLaborsChanged) {
            this._valDeducoesLabors = federalDeductions;
        }

        let issRetChanged = !Decimal(issDeduction).equals(this.retISS);

        if (issRetChanged) {
            this._retISS = issDeduction;
        }

        return { issRetChanged, valDeductionsLaborsChanged, valDeductionsPartsChanged };
    }

    // GROSS VALUE
    private _grossValue: number;
    public get grossValue(): number { return this._grossValue; }
    public set grossValue(value: number) { this._grossValue = value; }

    public get defaultClientWithSomeFiscalInvoice() {
        const fiscalLaborDoc = this.operation?.docFiscalServico,
            fiscalPartDoc = this.operation?.docFiscal,
            isDefaultClient = ClientUtilities.isDefault(this.client?.document),
            haveSomeLabor = this.approvedLabors?.length || this.thirdPartyServices?.length,
            liquidValuePartsHigherThanUnknownConsumerLimit = this.liquidValueParts > NFCE_CONSUMER_ISSUE_LIMIT;

        return (isDefaultClient &&
            (
                (fiscalLaborDoc && haveSomeLabor) ||
                (fiscalPartDoc && liquidValuePartsHigherThanUnknownConsumerLimit)
            )
        )
    }

    public get defaultVehicleWithFiscalLaborInvoice() {
        return VehicleUtilities.isDefault(this.vehicle.plate) && this.operation.docFiscalServico;
    }

    // LIQUID VALUE
    private _liquidValue: number;
    public get liquidValue(): number { return this._liquidValue; }
    public set liquidValue(value: number) { this._liquidValue = value; }
    /**
     * Define o valor líquido total da OS.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO recalcula valores derivados (valueDiscountOs, generalDiscount)
     * - Você DEVE chamar `roService.updateDiscount(os, 'netValue')` após alterar
     * - Backend calculará todos os valores derivados e redistribuição entre itens
     * 
     * @param value - Valor líquido em reais (já calculado pelo backend)
     */
    public setLiquidValue(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setLiquidValue: valor inválido:', value, '- usando valor atual');
            return;
        }

        // Apenas armazenar - backend calcula tudo
        this._liquidValue = Money(value);
        // valueDiscountOs e generalDiscount devem vir do backend junto com este valor
    }

    // GENERAL DISCOUNT VALUE OF OS
    private _valueDiscountOs: number;
    public get valueDiscountOs(): number { return this._valueDiscountOs || 0; }
    public set valueDiscountOs(value: number) { this._valueDiscountOs = value; }
    /**
     * Define o valor absoluto de desconto geral da OS.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO calcula percentual (generalDiscount)
     * - Você DEVE chamar `roService.updateDiscount(os, 'absolute')` após alterar
     * - Backend calculará: generalDiscount e aplicação em todos os itens
     * 
     * @param value - Valor em reais (será limitado >= 0)
     */
    public setValueDiscountOs(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setValueDiscountOs: valor inválido:', value, '- usando 0');
            this._valueDiscountOs = 0;
            return;
        }

        // Não permitir negativo
        const normalized = Math.max(0, value);

        if (normalized !== value) {
            console.warn(`[MkgOS] setValueDiscountOs: valor ${value} normalizado para ${normalized}`);
        }

        this._valueDiscountOs = Money(normalized);
        // Percentual virá do backend após updateDiscount()
    }

    // GENERAL DISCOUNT PERCENTAGE OF OS
    private _generalDiscount: number;
    public get generalDiscount(): number { return this._generalDiscount; }
    public set generalDiscount(value: number) { this._generalDiscount = value; }
    /**
     * Define o percentual de desconto geral da OS.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO calcula valores derivados (valueDiscountOs)
     * - Você DEVE chamar `roService.updateDiscount(os, 'percentage')` após alterar
     * - Backend calculará: valueDiscountOs e aplicação em todos os itens
     * 
     * @param value - Percentual de desconto (será limitado entre 0 e 100)
     */
    public setGeneralDiscount(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setGeneralDiscount: valor inválido:', value, '- usando 0');
            this._generalDiscount = 0;
            return;
        }

        // Limitar entre 0 e 100
        const normalized = Math.max(0, Math.min(100, value));

        if (normalized !== value) {
            console.warn(`[MkgOS] setGeneralDiscount: valor ${value} normalizado para ${normalized}`);
        }

        this._generalDiscount = normalized;
        // Valores derivados virão do backend após updateDiscount()
    }

    // Método removido: _getPartsLiquidValue1() - cálculos agora feitos apenas no backend

    // Método removido: _getPartsLiquidValue2() - cálculos agora feitos apenas no backend

    // Método removido: _getLaborLiquid1() - cálculos agora feitos apenas no backend

    // Método removido: _getLaborLiquid2() - cálculos agora feitos apenas no backend

    // Método removido: _getLaborsLiquidValue1() - cálculos agora feitos apenas no backend

    // Método removido: _getLaborsLiquidValue2() - cálculos agora feitos apenas no backend

    // GROSS VALUE OF PARTS
    private _grossValueParts: number;
    public get grossValueParts(): number { return this._grossValueParts }
    public set grossValueParts(value: number) { this._grossValueParts = value }
    // Método removido: _getGrossValueParts() - cálculos agora feitos apenas no backend
    // GROSS VALUE OF LABORS
    private _grossValueLabors: number;
    public get grossValueLabors(): number { return this._grossValueLabors }
    public set grossValueLabors(value: number) { this._grossValueLabors = value }
    // Método removido: _getGrossValueLabors() - cálculos agora feitos apenas no backend

    // PERCENTAGE OF DISCOUNT FOR PARTS
    private _discountParts: number;
    public get discountParts(): number { return this._discountParts }
    public set discountParts(value: number) { this._discountParts = value }
    /**
     * Define o percentual de desconto de peças.
     * ⚠️ NÃO recalcula - valores devem vir do backend já calculados.
     * @param value - Percentual do desconto (ex: 10 para 10%)
     */
    /**
     * Define o percentual de desconto de peças.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO calcula valores derivados (valueDiscountParts)
     * - Você DEVE chamar `roService.updateDiscount(os, 'percentage')` após alterar
     * - Backend calculará: valueDiscountParts, liquidValueParts e redistribuição entre itens
     * 
     * @param value - Percentual de desconto (será limitado entre 0 e 100)
     */
    public setDiscountParts(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setDiscountParts: valor inválido:', value, '- usando 0');
            this._discountParts = 0;
            return;
        }

        // Limitar entre 0 e 100
        const normalized = Math.max(0, Math.min(100, value));

        if (normalized !== value) {
            console.warn(`[MkgOS] setDiscountParts: valor ${value} normalizado para ${normalized}`);
        }

        this._discountParts = Money(normalized);
        // Valores derivados virão do backend após updateDiscount()
    }
    // PERCENTAGE OF DISCOUNT FOR LABORS
    private _discountLabors: number;
    public get discountLabors(): number { return this._discountLabors }
    public set discountLabors(value: number) { this._discountLabors = value }
    /**
     * Define o percentual de desconto de serviços.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO calcula valores derivados (valueDiscountLabors)
     * - Você DEVE chamar `roService.updateDiscount(os, 'percentage')` após alterar
     * - Backend calculará: valueDiscountLabors, liquidValueLabors e redistribuição entre itens
     * 
     * @param value - Percentual de desconto (será limitado entre 0 e 100)
     */
    public setDiscountLabors(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setDiscountLabors: valor inválido:', value, '- usando 0');
            this._discountLabors = 0;
            return;
        }

        // Limitar entre 0 e 100
        const normalized = Math.max(0, Math.min(100, value));

        if (normalized !== value) {
            console.warn(`[MkgOS] setDiscountLabors: valor ${value} normalizado para ${normalized}`);
        }

        this._discountLabors = Money(normalized);
        // Valores derivados virão do backend após updateDiscount()
    }

    // VALUE OF DISCOUNT FOR PARTS
    private _valueDiscountParts: number;
    public get valueDiscountParts(): number { return this._valueDiscountParts || 0 }
    public set valueDiscountParts(value: number) { this._valueDiscountParts = value }
    /**
     * Define o valor absoluto de desconto de peças.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO calcula percentual (discountParts)
     * - Você DEVE chamar `roService.updateDiscount(os, 'absolute')` após alterar
     * - Backend calculará: discountParts, liquidValueParts e redistribuição entre itens
     * 
     * @param value - Valor em reais (será limitado >= 0)
     */
    public setValueDiscountParts(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setValueDiscountParts: valor inválido:', value, '- usando 0');
            this._valueDiscountParts = 0;
            return;
        }

        // Não permitir negativo
        const normalized = Math.max(0, value);

        if (normalized !== value) {
            console.warn(`[MkgOS] setValueDiscountParts: valor ${value} normalizado para ${normalized}`);
        }

        this._valueDiscountParts = Money(normalized);
        // Percentual virá do backend após updateDiscount()
    }
    // VALUE OF DISCOUNT FOR LABORS
    private _valueDiscountLabors: number;
    public get valueDiscountLabors(): number { return this._valueDiscountLabors || 0 }
    public set valueDiscountLabors(value: number) { this._valueDiscountLabors = value }
    /**
     * Define o valor absoluto de desconto de serviços.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO calcula percentual (discountLabors)
     * - Você DEVE chamar `roService.updateDiscount(os, 'absolute')` após alterar
     * - Backend calculará: discountLabors, liquidValueLabors e redistribuição entre itens
     * 
     * @param value - Valor em reais (será limitado >= 0)
     */
    public setValueDiscountLabors(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setValueDiscountLabors: valor inválido:', value, '- usando 0');
            this._valueDiscountLabors = 0;
            return;
        }

        // Não permitir negativo
        const normalized = Math.max(0, value);

        if (normalized !== value) {
            console.warn(`[MkgOS] setValueDiscountLabors: valor ${value} normalizado para ${normalized}`);
        }

        this._valueDiscountLabors = Money(normalized);
        // Percentual virá do backend após updateDiscount()
    }

    // LIQUID VALUE FOR PARTS
    private _liquidValueParts: number;
    public get liquidValueParts(): number { return this._liquidValueParts }
    public set liquidValueParts(value: number) { this._liquidValueParts = value }
    /**
     * Define o valor líquido de peças.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO recalcula valores derivados (valueDiscountParts, discountParts)
     * - Você DEVE chamar `roService.updateDiscount(os, 'netValue')` após alterar
     * - Backend calculará todos os valores derivados
     * 
     * @param value - Valor líquido em reais
     */
    public setLiquidValueParts(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setLiquidValueParts: valor inválido:', value, '- usando valor atual');
            return;
        }

        // Não permitir negativo
        const normalized = Math.max(0, value);

        if (normalized !== value) {
            console.warn(`[MkgOS] setLiquidValueParts: valor ${value} normalizado para ${normalized}`);
        }

        this._liquidValueParts = Money(normalized);
        // Valores derivados virão do backend após updateDiscount()
    }
    // LIQUID VALUE FOR LABORS
    private _liquidValueLabors: number;
    public get liquidValueLabors(): number { return this._liquidValueLabors }
    public set liquidValueLabors(value: number) { this._liquidValueLabors = value }
    /**
     * Define o valor líquido de serviços.
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS armazena o valor localmente
     * - NÃO recalcula valores derivados (valueDiscountLabors, discountLabors)
     * - Você DEVE chamar `roService.updateDiscount(os, 'netValue')` após alterar
     * - Backend calculará todos os valores derivados
     * 
     * @param value - Valor líquido em reais
     */
    public setLiquidValueLabors(value: number) {
        // Validar tipo
        if (!Number.isFinite(value)) {
            console.warn('[MkgOS] setLiquidValueLabors: valor inválido:', value, '- usando valor atual');
            return;
        }

        // Não permitir negativo
        const normalized = Math.max(0, value);

        if (normalized !== value) {
            console.warn(`[MkgOS] setLiquidValueLabors: valor ${value} normalizado para ${normalized}`);
        }

        this._liquidValueLabors = Money(normalized);
        // Valores derivados virão do backend após updateDiscount()
    }

    /** The CNPJ of company where the OS is from */
    cnpj?: string;

    private _stateGroup: RoStateGroup;
    get stateGroup() {
        return this._stateGroup;
    }

    private _currentState: number;
    set currentState(value: number) {
        switch (value) {
            case RO_STATES.budget: // 2
            case RO_STATES.authorization: // 4
            case RO_STATES.appointment: // 5
            case RO_STATES.readyToStart: // 6
            case RO_STATES.closedBudget:
                this._stateGroup = 'budget';
                break;
            case RO_STATES.started: // 7
                this._stateGroup = 'opened';
                break;
            case RO_STATES.toReceive: // 8
            case RO_STATES.evaluation: // 9
                this._stateGroup = 'closed';
                break;
            case RO_STATES.finished: // 10
                this._stateGroup = 'ended';
                break;
            case RO_STATES.rejected: // 11
                this._stateGroup = 'rejected';
                break;
            default:
                this._stateGroup = "ended";
                break;
        }
        this._currentState = value;
    }
    get currentState() {
        return this._currentState;
    }

    constructor(apiOS: ApiOS & { cnpj?: string, note?: string }, dontDivideByOneHundred?: boolean) {
        const { parts: apiParts, note: apiNote, ...assignableApiOS } = apiOS;

        Object.assign(this, assignableApiOS);

        if (apiNote && !this.note) {
            this.vehicleTmp = `${this.vehicleTmp || ''}${JOIN_CHAR}${apiNote}`;
        }

        this._valDeducoesParts;

        if (apiOS.client && apiOS.client.email) {
            this.client.email = toASCII((apiOS.client.email || '').trim());
        }

        if (Number.isFinite(apiOS.liquidValue)) {
            if (moment(apiOS.lastUpdate).isSameOrBefore(moment('2024-09-01'))) {
                this.setLiquidValue(Money(apiOS.liquidValue / (dontDivideByOneHundred ? 1 : 100)));
            } else {
                this.liquidValue = Money(apiOS.liquidValue / (dontDivideByOneHundred ? 1 : 100));
            }
        }

        /*
         * Only the exact rejected pair (currentState: FINISHED + authorizationType: REJECTED)
         * maps to the local pseudo-state 11 — authorizationType alone isn't enough, otherwise an
         * OS moved back to budget (or any other non-terminal state) while still carrying a
         * REJECTED authorizationType residue would incorrectly display as "Rejeitado" here while
         * still showing up as open everywhere else.
         */
        if (this.currentState === RO_STATES.finished && apiOS.authorizationType == authorizationTypes.rejected.id) {
            this.currentState = RO_STATES.rejected;
        }

        if (apiOS.budgeter) {
            this.budgeterObject = apiOS.budgeter;
            this.budgeter = apiOS.budgeter?._id;
        }

        if (apiOS.consultant) {
            this.consultant = apiOS.consultant._id;
        }

        this.operationId = (apiOS.operation?._id ?? apiOS.operation?.id) ?? apiOS.operation as any;

        if (apiOS.operation && typeof apiOS.operation !== "string") {
            if (apiOS.operation.moveStock !== null && apiOS.operation.moveStock !== undefined) {
                this.operation.changeStock = apiOS.operation.moveStock;
            }

            if (apiOS.operation.financialGenera !== null && apiOS.operation.financialGenera !== undefined) {
                this.operation.generateFinancial = apiOS.operation.financialGenera;
            }

            if (apiOS.operation.inOut !== null && apiOS.operation.inOut !== undefined) {

            }

            this.operation.id = apiOS.operation._id;
        }

        if (apiOS.type && typeof apiOS.type !== "string") {
            this.type.id = apiOS.type.id ?? apiOS.type._id;
            this.type.nfeCnpj = (apiOS.type.nfeCnpj || '').trim();
            this.type.nfseCnpj = (apiOS.type.nfseCnpj || '').trim();
            this.type.observation = (apiOS.type.observation || '').trim();
            this.type.checklistMediaQualityPreset = apiOS.type.checklistMediaQualityPreset;
        }

        this._initializeArrays();

        if (Array.isArray(apiParts)) {
            this._parts = apiParts.map(apiPart => new MkgOSPart(apiPart, !dontDivideByOneHundred));
        }

        if (this.nfseIndDest === null || this.nfseIndDest === undefined) this.nfseIndDest = 0;

        // sum partTMP values
        for (const partTMP of this.partsTMP) {
            const grossValue = Money((partTMP.amount || 0) * (partTMP.saleValue || 0));
            const discountValue = Money(partTMP.discountValue || 0);
            const discountProp = Money(partTMP.discountProp ?? partTMP._discountProp ?? 0);
            const discountPropGeral = Money(partTMP.discountPropGeral ?? partTMP._discountPropGeral ?? 0);

            partTMP.discountValue = discountValue;
            partTMP.discountProp = discountProp;
            partTMP.discountPropGeral = discountPropGeral;
            partTMP._discountProp = discountProp;
            partTMP._discountPropGeral = discountPropGeral;

            if (Number.isFinite(partTMP.liquidValue)) {
                partTMP._netValue = Money(partTMP.liquidValue);
            } else {
                partTMP._netValue = Money(grossValue - discountValue - discountProp - discountPropGeral);
                partTMP.liquidValue = partTMP._netValue;
            }

            const internalObservation = partTMP.internalObservation;
            const observation = partTMP.observation;
            partTMP.observation = internalObservation;
            partTMP.internalObservation = observation;
        }

        for (const roLabor of this.labors) {
            roLabor.saleValue = (roLabor.saleValue || 0) / (dontDivideByOneHundred ? 1 : 100);
            roLabor.discountValue = (roLabor.discountValue || 0) / (dontDivideByOneHundred ? 1 : 100);
            roLabor.liquidValue = (roLabor.liquidValue || 0) / (dontDivideByOneHundred ? 1 : 100);
            roLabor.discountProp = (roLabor.discountProp || 0) / (dontDivideByOneHundred ? 1 : 100);
            roLabor.discountPropGeral = (roLabor.discountPropGeral || 0) / (dontDivideByOneHundred ? 1 : 100);

            /**
             * Está sendo invertido o campo de observações para que as observações
             * que ja existiam (campo 'observation') aparecam como padrão no pdf
            */
            const observation = roLabor.observation ?? "";
            roLabor.observation = roLabor.internalObservation ?? "";
            roLabor.internalObservation = observation;
        }

        // sum laborTMP values
        for (const laborTMP of this.laborsTMP) {
            const grossValue = Money((laborTMP.amount || 0) * (laborTMP.saleValue || 0));
            const discountValue = Money(laborTMP.discountValue || 0);
            const discountProp = Money(laborTMP.discountProp ?? laborTMP._discountProp ?? 0);
            const discountPropGeral = Money(laborTMP.discountPropGeral ?? laborTMP._discountPropGeral ?? 0);

            laborTMP.discountValue = discountValue;
            laborTMP.discountProp = discountProp;
            laborTMP.discountPropGeral = discountPropGeral;
            laborTMP._discountProp = discountProp;
            laborTMP._discountPropGeral = discountPropGeral;

            if (Number.isFinite(laborTMP.liquidValue)) {
                laborTMP._netValue = Money(laborTMP.liquidValue);
            } else {
                laborTMP._netValue = Money(grossValue - discountValue - discountProp - discountPropGeral);
                laborTMP.liquidValue = laborTMP._netValue;
            }

            const internalObservation = laborTMP.internalObservation;
            const observation = laborTMP.observation;
            laborTMP.observation = internalObservation;
            laborTMP.internalObservation = observation;
        }

        for (const outsourced of this.thirdPartyServices) {
            outsourced.id = outsourced._id;
            outsourced.collectedValue = (outsourced.collectedValue || 0) / (dontDivideByOneHundred ? 1 : 100);
            outsourced.paymentValue = (outsourced.paymentValue || 0) / (dontDivideByOneHundred ? 1 : 100);
            if (!outsourced.discountPropLabor) {
                outsourced.discountPropLabor = 0; // prevent NaN
            }
            if (!outsourced.discountPropGeral) {
                outsourced.discountPropGeral = 0; // prevent NaN
            }
        }

        // Onda1·E1·T4: `receipts.value` e `lows[].value` chegam em centavos (mesma unidade de
        // `liquidValue`, confirmado em `companies-lows.js`) só quando o recibo já existe (OS
        // aprovada) — string (id não populado) ou ausente ficam como vieram, sem normalizar.
        if (this.receipts && typeof this.receipts === 'object') {
            const receipt = this.receipts;
            if (Number.isFinite(receipt.value)) {
                receipt.value = (receipt.value || 0) / (dontDivideByOneHundred ? 1 : 100);
            }
            if (Array.isArray(receipt.lows)) {
                for (const low of receipt.lows) {
                    if (Number.isFinite(low.value)) {
                        low.value = (low.value || 0) / (dontDivideByOneHundred ? 1 : 100);
                    }
                }
            }
        }

        // Backend já envia grossValueParts e grossValueLabors calculados
        // Se não vieram, significa que é uma OS antiga - inicializar com 0
        if (!Number.isFinite(apiOS.grossValueParts)) {
            console.warn('[MkgOS] Constructor: grossValueParts não recebido do backend - inicializando com 0');
            this.grossValueParts = 0;
        }

        if (!Number.isFinite(apiOS.grossValueLabors)) {
            console.warn('[MkgOS] Constructor: grossValueLabors não recebido do backend - inicializando com 0');
            this.grossValueLabors = 0;
        }

        if (!Number.isFinite(apiOS.grossValue)) {
            this.grossValue = this.grossValueParts + this.grossValueLabors;
        }

        if (!Number.isFinite(apiOS.generalDiscount)) {
            this.generalDiscount = Utilities.getPercentage(this.valueDiscountOs || 0, this.sumDiscountValues.baseGeral());
        }

        // Backend já envia todos os valores calculados e redistribuídos
        // Não há necessidade de recalcular localmente

        // adjusting liquid values (apenas para garantir consistência)
        for (const labor of this.labors) {
            labor.liquidValue = (labor.amount * labor.saleValue) - labor.discountValue - labor.discountProp - labor.discountPropGeral;
        }

        if (!Number.isFinite(apiOS.liquidValue)) {
            this.liquidValue = this.liquidValueParts + this.liquidValueLabors;
        }

        // Valores já vêm prontos do backend - não recalcular

        if (this.checklistModelItem) {
            this.checklistModel = CHECKLIST_OPTIONS.BY_MODEL;
        } else if (this.checklist?.length) {
            this.checklistModel = CHECKLIST_OPTIONS.COMMON;
        } else {
            this.checklistModel = apiOS.checklistModel ?? CHECKLIST_OPTIONS.UNSET;
        }

        combineLatest([
            this.nfInvoices$.pipe(skip(1)),
            this.nfseInvoices$.pipe(skip(1))
        ]).subscribe((() => {
            this.areInvoicesConsulted$.next(true);
        }));

        if (!this.id) {
            this.id = apiOS.id ?? apiOS["_id"];
        }
    }

    /** Prevent error: reduce of empty array */
    private _initializeArrays() {
        if (!Array.isArray(this.parts))
            this._parts = [];
        if (!Array.isArray(this.partsTMP))
            this.partsTMP = [];
        if (!Array.isArray(this.labors))
            this.labors = [];
        if (!Array.isArray(this.laborsTMP))
            this.laborsTMP = [];
        if (!Array.isArray(this.thirdPartyServices))
            this.thirdPartyServices = [];
        if (!Array.isArray(this.paymentConditions))
            this.paymentConditions = [];
        if (!Array.isArray(this.chat))
            this.chat = [];
        if (!Array.isArray(this.tags))
            this.tags = [];
        if (!Array.isArray(this.checklist))
            this.checklist = [];
        if (!Array.isArray(this.notes))
            this.notes = [];
        if (!Array.isArray(this.importsBudget))
            this.importsBudget = [];
    }

    /** The not-rejected parts */
    get availableParts() {
        return this.parts.filter(p => p.available != partStates.rejected.id);
    }

    get approvedParts() {
        return this.parts.filter(p => p.available == partStates.approved.id);
    }

    /** the not-rejected partsTMP */
    get availablePartsTMP() {
        return this.partsTMP.filter(ptmp => ptmp.status != DESCRIPTIVE_ITEM_STATUS.INACTIVE);
    }

    /** The not rejected labors */
    get availableLabors() {
        return this.labors.filter(l => l.available != laborStates.rejected.id);
    }

    get approvedLabors() {
        return this.labors.filter(l => l.available == laborStates.approved.id);
    }

    /** The not rejected laborsTMP */
    get availableLaborsTMP() {
        return this.laborsTMP.filter(ltmp => ltmp.status != DESCRIPTIVE_ITEM_STATUS.INACTIVE);
    }

    /** Sum of collectedValue of thirdPartyServices */
    get outsourcedValue(): number {
        return this.thirdPartyServices.reduce((acc, out) => acc + out.collectedValue, 0)
    }

    /** Sum of paid of thirdPartyServices */
    get outsourcedPaidValue(): number {
        return this.thirdPartyServices.reduce((acc, out) => acc + out.paymentValue, 0)
    }

    public getClientFiscalName(notFoundLabel = "") {
        if (ClientUtilities.isDefault(this.client?.document) && this.clientTmp) {
            return this.clientTmp;
        }
        return this.client?.name ?? notFoundLabel;
    }

    sumDiscountValues = {
        partsTMP: {
            propParts: () => this.availablePartsTMP.reduce((acc, part) => Money(Money(acc) + Money(part._discountProp)), 0),
            propGeral: () => this.availablePartsTMP.reduce((acc, part) => Money(Money(acc) + Money(part._discountPropGeral)), 0)
        },
        parts: {
            additional: () => this.availableParts.reduce((acc, part) => Money(Money(acc) + Money(part.discountValue())), 0),
            propParts: () => this.availableParts.reduce((acc, part) => Money(Money(acc) + Money(part.discountProp())), this.sumDiscountValues.partsTMP.propParts()),
            propGeral: () => this.availableParts.reduce((acc, part) => Money(Money(acc) + Money(part.discountPropGeral())), this.sumDiscountValues.partsTMP.propGeral()),
            totalDiscountParts: () => Money(this.sumDiscountValues.parts.additional() + this.sumDiscountValues.parts.propParts() + this.sumDiscountValues.parts.propGeral())
        },
        laborsTMP: {
            propLabors: () => this.availableLaborsTMP.reduce((acc, labor) => Money(Money(acc) + Money(labor._discountProp)), 0),
            propGeral: () => this.availableLaborsTMP.reduce((acc, labor) => Money(Money(acc) + Money(labor._discountPropGeral)), 0)
        },
        labors: {
            additional: () => this.availableLabors.reduce((acc, labor) => Money(Money(acc) + Money(labor.discountValue)), 0),
            propLabors: () => this.availableLabors.reduce((acc, labor) => Money(Money(acc) + Money(labor.discountProp)), this.sumDiscountValues.laborsTMP.propLabors() + this.sumDiscountValues.outsourced.sumOutsourcedDiscountLabors()),
            propGeral: () => this.availableLabors.reduce((acc, labor) => Money(Money(acc) + Money(labor.discountPropGeral)), Money(this.sumDiscountValues.laborsTMP.propGeral() + this.sumDiscountValues.outsourced.sumOutsourcedDiscountGeral()))
        },
        outsourced: {
            // sumOutsourcedCollectedValue: () => this.thirdPartyServices.reduce((acc, out) => Money(acc) + Money(out.collectedValue || 0), 0),
            sumOutsourcedDiscountLabors: () => this.thirdPartyServices.reduce((acc, out) => Money(Money(acc) + Money(out.discountPropLabor || 0)), 0),
            sumOutsourcedDiscountGeral: () => this.thirdPartyServices.reduce((acc, out) => Money(Money(acc) + Money(out.discountPropGeral || 0)), 0)
        },
        /**
         * Base de cálculo para desconto geral.
         * Usa valores já calculados pelo backend (liquidValueParts + liquidValueLabors + descontos já aplicados).
         * 
         * NOTA: Este cálculo é usado apenas para cálculos legados.
         * Backend é a fonte de verdade para todos os valores.
         */
        baseGeral: () => {
            // Usar valores já calculados pelo backend
            const partsBase = Money(this.liquidValueParts + this.valueDiscountParts);
            const laborsBase = Money(this.liquidValueLabors + this.valueDiscountLabors);
            return Money(partsBase + laborsBase);
        },
    }

    get isBudgetOrRejected(): boolean {
        return ["budget", "rejected"].includes(this.stateGroup);
    }

    get isBudget(): boolean {
        return this.stateGroup == "budget" && (this._currentState !== RO_STATES.closedBudget);
    }

    get isOS(): boolean {
        return ["opened", "closed", "ended"].includes(this.stateGroup);
    }

    /** budgetCode or codeSystem, according state */
    get relativeCode(): number {
        return this.isBudgetOrRejected ? this.budgetCode : this.codeSystem;
    }

    get isRejected(): boolean {
        return this.stateGroup === "rejected";
    }

    get isOpened() {
        return this.stateGroup === "opened";
    }

    get isClosed() {
        return this.stateGroup === "closed";
    }

    get isEnded() {
        return this.stateGroup === "ended";
    }

    get canImportItems() {
        return this._currentState === RO_STATES.appointment;
    }

    get isClosedOrEnded(): boolean {
        return ["closed", "ended"].includes(this.stateGroup);
    }

    get isClosedOrEndedOrRejected(): boolean {
        return ["closed", "ended", "rejected"].includes(this.stateGroup);
    }

    get paymentConditionsSum() {
        return this.paymentConditions.reduce((sum, condition) => sum + condition.value, 0);
    }

    get restantToPay() {
        return Money(
            Number((
                this.liquidValue
                - this.paymentConditionsSum
                - this.retISS
                - this.valDeducoesLabors
                - this.valDeducoesParts
            ).toFixed(3))
        );
    }

    get maxDiscountForParts() {
        return Money(this.grossValueParts - this.sumDiscountValues.parts.additional());
    }

    get maxDiscountForLabors() {
        return Money(this.grossValueLabors - this.sumDiscountValues.labors.additional());
    }

    /**
     * indicate if the user can modify the OS values
     *  - is company integrated and current state OS
     */
    get canEdit() {
        return this.isBudget || (this.isOpened && !this.isIntegratedCompany);
    }

    setIsIntegratedCompany(integrated: boolean) {
        this.isIntegratedCompany = integrated;
    }

    static fromForm(formValue: MkgOSFromFormInput): MkgOSFromFormDraft {
        const OS: MkgOSFromFormDraft = {
            user: formValue.user,
            km: formValue.km,
            deliveryForecast: formValue.deliveryForecast,
            problem: formValue.problem,
            prisma: formValue.prisma,
            clientTmp: formValue.clientTmp,
            vehicleTmp: formValue.note ? `${formValue.vehicleTmp}${JOIN_CHAR}${formValue.note}` : formValue.vehicleTmp,
            telefTmp: formValue.telefTmp,
            currentState: formValue.currentState,
            consultant: formValue.consultant
        }

        if (formValue.id) {
            OS.id = formValue.id;
        }

        if (formValue.type) {
            OS.type = formValue.type;
        }

        if (formValue.vehicle) {
            OS.vehicle = formValue.vehicle;
        }

        if (formValue.client) {
            OS.client = formValue.client;
        }

        if (formValue.operation) {
            OS.operation = formValue.operation;
        }

        return OS
    }

    checkPartsValues(isMobile: boolean, services: {
        dialog: MatDialog,
        layoutService: LayoutService
    }, compareToZero = false): boolean {
        const partsWithAverageOrPurchaseCostHigherThanLiquidValueOrEqualZero: Array<{
            part: Part;
            liquidValue: number;
        }> = [];
        const { dialog, layoutService } = services;

        layoutService.addLoaderTrigger("checkPartsValues()");

        const registeredParts = this.availableParts.map(part => ({
            part: part.part,
            liquidValue: (part.liquidValue() / part.amount())
        }))

        registeredParts.forEach(part => {
            const { cost, averageCost } = part.part;
            const { liquidValue } = part;

            if ((cost > liquidValue) || (averageCost > liquidValue)) {
                partsWithAverageOrPurchaseCostHigherThanLiquidValueOrEqualZero.push(part);
            } else if (compareToZero && ((cost === 0) || averageCost === 0)) {
                partsWithAverageOrPurchaseCostHigherThanLiquidValueOrEqualZero.push(part);
            };
        });


        if (partsWithAverageOrPurchaseCostHigherThanLiquidValueOrEqualZero.length) {
            layoutService.removeLoaderTrigger("checkPartsValues()");

            dialog.open(AveragePurchaseCostAlertComponent, {
                data: {
                    parts: partsWithAverageOrPurchaseCostHigherThanLiquidValueOrEqualZero,
                    isMobile: isMobile,
                },
                minWidth: isMobile ? "90vw" : "30rem",
                minHeight: isMobile ? "90vh" : "25rem",
                maxWidth: isMobile ? "95vw" : "",
                maxHeight: isMobile ? "95vh" : "",
            });
        };

        layoutService.removeLoaderTrigger("checkPartsValues()");
        return partsWithAverageOrPurchaseCostHigherThanLiquidValueOrEqualZero.length > 0;
    }


    /**
     * Zera todos os descontos (geral, peças e serviços).
     * 
     * ⚠️ **IMPORTANTE:**
     * - Este método APENAS zera os valores localmente
     * - Você DEVE chamar `roService.updateDiscount(os, 'percentage')` após usar este método
     * - Backend recalculará todos os valores e redistribuirá entre itens
     * 
     * @example
     * ```typescript
     * ro.setGeneralDiscountsToZero();
     * const updated = await roService.updateDiscount(ro, 'percentage');
     * Object.assign(ro, updated); // Atualizar instância local
     * ```
     */
    setGeneralDiscountsToZero() {
        this.setValueDiscountLabors(0);
        this.setValueDiscountParts(0);
        this.setValueDiscountOs(0);
    }

    /**
     * Verifica se um campo é calculado pelo backend e não deve ser enviado em atualizações.
     * 
     * Campos calculados incluem:
     * - Valores totais (grossValue, liquidValue)
     * - Valores por categoria (grossValueParts, liquidValueParts, etc)
     * - Descontos calculados (discountParts, discountLabors, generalDiscount)
     * - Valores absolutos de desconto (valueDiscountParts, valueDiscountLabors, valueDiscountOs)
     * 
     * @param field Nome do campo a verificar
     * @returns true se o campo é calculado pelo backend
     */
    private _isCalculatedField(field: string): boolean {
        const calculatedFields = [
            'discountLabors',
            'discountParts',
            'generalDiscount',
            'grossValue',
            'grossValueLabors',
            'grossValueParts',
            'liquidValue',
            'liquidValueLabors',
            'liquidValueParts',
            'valueDiscountLabors',
            'valueDiscountOs',
            'valueDiscountParts'
        ];
        return calculatedFields.includes(field);
    }

    /**
     * Serializa OS para CRIAR nova OS no backend.
     * Envia apenas inputs do usuário, backend calculará todos os valores derivados.
     * 
     * ⚠️ Use este método ao invés de toAPI() para criar novas OSs.
     * 
     * @returns Objeto contendo apenas campos de entrada do usuário
     */
    toAPICreate(): Partial<ApiOSPost> {
        return {
            // Dados básicos
            client: this.client?.id,
            vehicle: this.vehicle?.id,
            operation: this.operationId,
            type: this.type?.id,

            // Descrição e estado
            problem: this.problem,
            km: this.km,
            currentState: this.currentState,

            // Dados temporários (cliente/veículo padrão)
            clientTmp: this.clientTmp,
            vehicleTmp: this._vehicleTmp, // com JOIN_CHAR e nota de máquina
            telefTmp: this.telefTmp,

            // Outros campos
            prisma: this.prisma,
            user: this.user,
            observation: this.observation,
            observationNFSE: this.observationNFSE,

            // Itens (backend calculará valores derivados)
            parts: this.parts?.slice().map(p => p.toAPI()),
            labors: this.labors as any,
            partsTMP: this.partsTMP,
            laborsTMP: this.laborsTMP,
            thirdPartyServices: this.thirdPartyServices,

            // Configurações
            checklistModel: this.checklistModel ?? CHECKLIST_OPTIONS.UNSET,
            presence: this.presence,

            // Backend calculará automaticamente:
            // - grossValue, grossValueParts, grossValueLabors
            // - liquidValue, liquidValueParts, liquidValueLabors
            // - discountParts, discountLabors, generalDiscount
            // - valueDiscountParts, valueDiscountLabors, valueDiscountOs
        };
    }

    /**
     * Serializa OS para ATUALIZAR campos específicos no backend.
     * Envia apenas campos modificados pelo usuário.
     * 
     * ⚠️ Use este método ao invés de toAPI() para atualizar OSs existentes.
     * ⚠️ NÃO envia valores calculados (use roService.updateDiscount() para descontos)
     * 
     * @param fields Array com nomes dos campos que foram modificados
     * @returns Objeto contendo apenas os campos especificados
     * 
     * @example
     * ```typescript
     * // Atualizar apenas o problema e km
     * const payload = os.toAPIUpdate(['problem', 'km']);
     * await roService.update(os.id, payload);
     * ```
     */
    toAPIUpdate(fields: (keyof MkgOS)[]): Partial<ApiOSPost> {
        const payload: any = {
            lastUpdate: new Date().toISOString()
        };

        fields.forEach(field => {
            // Pular campos calculados
            if (this._isCalculatedField(field as string)) {
                console.warn(`[MkgOS] toAPIUpdate: ignorando campo calculado '${field}'. Use roService.updateDiscount() para atualizar descontos.`);
                return;
            }

            // Mapear campos que têm nomes diferentes na API
            if (field === 'client' && this.client?.id) {
                payload.client = this.client.id;
            } else if (field === 'vehicle' && this.vehicle?.id) {
                payload.vehicle = this.vehicle.id;
            } else if (field === 'operation' && this.operationId) {
                payload.operation = this.operationId;
            } else if (field === 'type' && this.type?.id) {
                payload.type = this.type.id;
            } else if (field === 'parts' && this.parts) {
                payload.parts = this.parts.slice().map(p => p.toAPI());
            } else if (field in this) {
                // @ts-ignore - acesso dinâmico
                payload[field] = this[field];
            }
        });

        return payload;
    }

    /**
     * Serializa OS completa para envio ao backend.
     * 
     * ⚠️ **DEPRECATED:** Este método envia valores calculados que deveriam vir apenas do backend.
     * 
     * **Use ao invés:**
     * - `toAPICreate()` para criar novas OSs
     * - `toAPIUpdate(['field1', 'field2'])` para atualizar campos específicos
     * - `roService.updateDiscount()` para atualizar descontos
     * 
     * @deprecated Use toAPICreate() ou toAPIUpdate() conforme o caso
     * @returns Objeto completo da OS (inclui valores calculados - NÃO recomendado)
     */
    toAPI(): ApiOSPost {
        console.warn('[MkgOS] toAPI() está deprecated. Use toAPICreate() ou toAPIUpdate() conforme o caso.');

        return {
            breakdowns: this.breakdowns,
            budgetCode: this.budgetCode,
            chat: this.chat,
            checklist: this.checklist,
            client: this.client.id,
            clientTmp: this.clientTmp,
            code: this.code,
            companyInfo: this.companyInfo,
            companyLogo: this.companyLogo,
            createdAt: this.createdAt,
            currentState: this.currentState,
            discountLabors: this.discountLabors,
            discountParts: this.discountParts,
            generalDiscount: this.generalDiscount,
            grossValue: this.grossValue,
            grossValueLabors: this.grossValueLabors,
            grossValueParts: this.grossValueParts,
            id: this.id,
            km: this.km,
            labors: this.labors as any,
            laborsTMP: this.laborsTMP,
            lastUpdate: this.lastUpdate,
            liquidValue: this.liquidValue,
            liquidValueLabors: this.liquidValueLabors,
            liquidValueParts: this.liquidValueParts,
            notes: this.notes,
            operation: this.operationId,
            parts: this.parts.slice().map(p => p.toAPI()),
            partsTMP: this.partsTMP,
            paymentConditions: this.paymentConditions,
            prisma: this.prisma,
            problem: this.problem,
            receipts: this.receipts,
            shortUrl: this.shortUrl,
            tags: this.tags,
            thirdPartyServices: this.thirdPartyServices,
            type: this.type.id,
            updatedAt: this.updatedAt,
            user: this.user,
            valueDiscountLabors: this.valueDiscountLabors,
            valueDiscountOs: this.valueDiscountOs,
            valueDiscountParts: this.valueDiscountParts,
            vehicle: this.vehicle?.id,
            vehicleTmp: this._vehicleTmp,
            importsBudget: this.importsBudget,
            checklistModel: this.checklistModel ?? CHECKLIST_OPTIONS.UNSET,
        }
    }
}
